import React, { useState } from 'react';
import { ChevronDown, ArrowRight, CheckCircle2 } from 'lucide-react';
import { formatPHP } from '../../lib/utils';

const peso = (n) => formatPHP(Math.abs(parseFloat(n) || 0));
const signedPeso = (n) => (parseFloat(n) < 0 ? '-' : parseFloat(n) > 0 ? '+' : '') + peso(n);

const SEV = {
  critical: { dot: 'bg-red-500', text: 'text-red-700', chip: 'bg-red-50 text-red-700 border-red-200', label: 'Critical' },
  warning: { dot: 'bg-amber-500', text: 'text-amber-700', chip: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Warning' },
  watch: { dot: 'bg-blue-500', text: 'text-blue-700', chip: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Watch' },
  info: { dot: 'bg-slate-400', text: 'text-slate-600', chip: 'bg-slate-100 text-slate-600 border-slate-200', label: 'Info' },
};

function FindingRow({ f, onDrill }) {
  const [open, setOpen] = useState(false);
  const sev = SEV[f.severity] || SEV.info;
  const impact = parseFloat(f.amount_impact || 0);
  return (
    <div className="border-b border-slate-100 last:border-b-0" data-testid={`finding-row-${f.id}`}>
      <button onClick={() => setOpen((o) => !o)} data-testid="finding-row-expand-button"
        className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-slate-50 transition-colors text-left">
        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${sev.dot}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800 truncate">{f.title}</p>
          <p className="text-[11px] text-slate-400 mt-0.5 capitalize">{f.section}</p>
        </div>
        {impact !== 0 && (
          <span className={`font-mono text-sm font-semibold shrink-0 ${impact < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
            {signedPeso(impact)}
          </span>
        )}
        <span className={`shrink-0 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase border ${sev.chip} hidden sm:inline`}>{sev.label}</span>
        <ChevronDown size={16} className={`shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pl-9 space-y-3">
          {f.evidence?.length > 0 && (
            <div className="bg-slate-50 border border-slate-100 rounded-lg p-3">
              <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-2">Evidence</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                {f.evidence.map((e, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-xs">
                    <span className="text-slate-500">{e.label}</span>
                    <span className="font-mono font-semibold text-slate-700 text-right">{e.value}</span>
                  </div>
                ))}
              </div>
              {f.rule && <p className="font-mono text-[10px] text-slate-400 mt-2 pt-2 border-t border-slate-200">Rule: {f.rule}</p>}
            </div>
          )}
          {f.recommendation && (
            <div className="flex items-start gap-2">
              <CheckCircle2 size={14} className="text-[#1A4D2E] shrink-0 mt-0.5" />
              <p className="text-xs text-slate-600 leading-relaxed"><span className="font-semibold text-slate-700">What to do: </span>{f.recommendation}</p>
            </div>
          )}
          {f.drilldown && onDrill && (
            <button onClick={() => onDrill(f.drilldown)} data-testid="finding-drilldown-btn"
              className="text-xs font-semibold text-[#1A4D2E] flex items-center gap-1 hover:gap-2 transition-all">
              View the records <ArrowRight size={13} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function InvestigationQueue({ auditData, onDrill }) {
  const [filter, setFilter] = useState('all');
  if (!auditData) return null;
  const all = auditData.findings || [];
  const counts = auditData.findings_summary?.counts || {};
  const filtered = filter === 'all' ? all : all.filter((f) => f.severity === filter);

  const FILTERS = [
    { k: 'all', label: 'All', n: all.length },
    { k: 'critical', label: 'Critical', n: counts.critical || 0 },
    { k: 'warning', label: 'Warning', n: counts.warning || 0 },
    { k: 'watch', label: 'Watch', n: counts.watch || 0 },
  ];

  return (
    <div className="space-y-4" data-testid="investigation-queue">
      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map((ft) => (
          <button key={ft.k} onClick={() => setFilter(ft.k)} data-testid={`queue-filter-${ft.k}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
              filter === ft.k ? 'bg-[#1A4D2E] text-white border-[#1A4D2E]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
            }`}>
            {ft.label} <span className={`font-mono ${filter === ft.k ? 'text-white/80' : 'text-slate-400'}`}>{ft.n}</span>
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-10 flex flex-col items-center justify-center text-center">
            <CheckCircle2 size={32} className="text-emerald-500 mb-3" />
            <p className="font-semibold text-slate-700">Nothing to investigate{filter !== 'all' ? ` (${filter})` : ''}.</p>
            <p className="text-sm text-slate-400 mt-1">All clear for this filter. Findings are ranked by severity then peso impact.</p>
          </div>
        ) : (
          filtered.map((f) => <FindingRow key={f.id} f={f} onDrill={onDrill} />)
        )}
      </div>
    </div>
  );
}
